import classNames from 'classnames';
import React from 'react';
import { useMutation } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Badge from '../components/Badge';
import EmptyState from '../components/EmptyState';
import GroupSquarishCard from '../components/GroupSquarishCard';
import { MenuItem } from '../components/Menu';
import SquarishCardGrid from '../components/SquarishCardGrid';
import UserSquarishCard from '../components/UserSquarishCard';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { GroupUserRole, UserType } from '../graphql/generated/graphql';
import { groupToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import AdminBadge from '../user/AdminBadge';
import AddMemberControl from './AddMemberControl';
import * as styles from './GroupViewMembers.module.scss';
import groupPolicyCheck, {
  GroupPolicyAction,
  isUserInGroup,
} from './groupPolicyCheck';

gql(`
  fragment GroupViewMembers_member on User {
    id
    name
    ...UserSquarishCard_user
  }
`);

const USE_GROUP_MEMBERS_COLUMN_GROUP_FRAGMENT = gql(`
  fragment GroupViewMembers_group on Group {
    id
    owners {
      ...GroupViewMembers_member
    }
    members {
      ...GroupViewMembers_member
    }
    children {
      id
      name
      ...GroupSquarishCard_group
    }
    ...AddMemberControl_group
    ...groupPolicyCheck_group
  }
`);

const ADD_GROUP_TO_GROUP_MUTATION = gql(/** @complexity 6 */ `
  mutation GroupViewMembersAddGroupToGroup($parentId: ID!, $childId: ID!) {
    addGroupToGroup(parentId: $parentId, childId: $childId) {
      id
      children {
        id
      }
    }
  }
`);

const REMOVE_GROUP_FROM_GROUP_MUTATION = gql(/** @complexity 6 */ `
  mutation GroupViewMembersRemoveGroupFromGroup(
    $parentId: ID!
    $childId: ID!
  ) {
    removeGroupFromGroup(parentId: $parentId, childId: $childId) {
      id
      children {
        id
      }
    }
  }
`);

interface GroupViewMembersProps {
  group: DocumentType<typeof USE_GROUP_MEMBERS_COLUMN_GROUP_FRAGMENT>;
  handleAddUser: (id: string) => Promise<unknown>;
  handleUpdateUserRole: (id: string, role: GroupUserRole) => Promise<unknown>;
  handleRemoveUser: (id: string) => Promise<unknown>;
}

const GroupViewMembers = ({
  group,
  handleAddUser,
  handleUpdateUserRole,
  handleRemoveUser,
}: GroupViewMembersProps) => {
  const dispatch = useAppDispatch();
  const memberCount =
    group.owners.length + group.members.length + group.children.length;
  const currentUser = useAppSelector((state) => state.session.user);

  const isShowingMembershipButton = isUserInGroup(group, currentUser);

  const canEditMembership = groupPolicyCheck(
    GroupPolicyAction.EditMembership,
    currentUser,
    group,
  );

  const [addToGroupMutate] = useMutation(ADD_GROUP_TO_GROUP_MUTATION);
  const handleAddSubgroup = (childId: string) =>
    addToGroupMutate({
      variables: {
        parentId: group.id,
        childId,
      },
    });

  const [removeFromGroupMutate] = useMutation(REMOVE_GROUP_FROM_GROUP_MUTATION);

  const onPromoteUser = async (id: string, name: string) => {
    await handleUpdateUserRole(id, GroupUserRole.Owner);
    dispatch(
      UIActions.pushToast(groupToasts.userPromotedToOwner({ userName: name })),
    );
  };

  const onDemoteUser = async (id: string, name: string) => {
    await handleUpdateUserRole(id, GroupUserRole.Member);
    dispatch(
      UIActions.pushToast(groupToasts.userDemotedToMember({ userName: name })),
    );
  };

  const onRemoveUser = async (id: string, name: string) => {
    await handleRemoveUser(id);
    dispatch(UIActions.pushToast(groupToasts.userRemoved({ userName: name })));
  };

  const onRemoveSubgroup = async (id: string, name: string) => {
    await removeFromGroupMutate({
      variables: {
        parentId: group.id,
        childId: id,
      },
    });
    dispatch(
      UIActions.pushToast(groupToasts.subgroupRemoved({ groupName: name })),
    );
  };

  const getUserMenuItems = (userId: string, name: string, isOwner: boolean) => {
    if (
      !canEditMembership &&
      (currentUser.type !== UserType.Admin || userId !== currentUser.id)
    ) {
      return null;
    }

    const isSelf = userId === currentUser.id;

    return (
      <>
        {isOwner ? (
          <MenuItem onClick={() => onDemoteUser(userId, name)}>
            {isSelf ? 'Demote yourself' : 'Demote to member'}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => onPromoteUser(userId, name)}
            right={isSelf ? <AdminBadge /> : undefined}
          >
            {isSelf ? 'Become owner' : 'Promote to owner'}
          </MenuItem>
        )}
        <MenuItem danger onClick={() => onRemoveUser(userId, name)}>
          {isSelf ? 'Leave group' : 'Remove from group'}
        </MenuItem>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>Group Members</h3>
        <div
          className={classNames(styles.controls, {
            [styles.hidden]: !isShowingMembershipButton,
          })}
        >
          <AddMemberControl
            group={group}
            canEdit={canEditMembership}
            onAddUser={(id: string) => handleAddUser(id)}
            onAddGroup={(id: string) => handleAddSubgroup(id)}
          />
        </div>
      </div>
      <SquarishCardGrid>
        {group.owners.map((owner) => (
          <SquarishCardGrid.Item key={owner.id}>
            <UserSquarishCard
              user={owner}
              badge={<Badge color="defaultNoBorder">owner</Badge>}
              link
              overflowMenuItems={getUserMenuItems(owner.id, owner.name, true)}
            />
          </SquarishCardGrid.Item>
        ))}
        {group.members.map((member) => (
          <SquarishCardGrid.Item key={member.id}>
            <UserSquarishCard
              user={member}
              link
              overflowMenuItems={getUserMenuItems(
                member.id,
                member.name,
                false,
              )}
            />
          </SquarishCardGrid.Item>
        ))}
        {group.children.map((child) => (
          <SquarishCardGrid.Item key={child.id}>
            <GroupSquarishCard
              group={child}
              link
              overflowMenuItems={
                canEditMembership &&
                child?.id && (
                  <MenuItem
                    onClick={() => onRemoveSubgroup(child.id, child.name)}
                    danger
                  >
                    Delete group
                  </MenuItem>
                )
              }
            />
          </SquarishCardGrid.Item>
        ))}
      </SquarishCardGrid>
      {memberCount === 0 && (
        <EmptyState
          imageName="connections"
          title="No members yet..."
          message="Members will appear after they have been added"
        />
      )}
    </div>
  );
};

export default GroupViewMembers;
